import React, { useMemo } from 'react';
import { RichTextDescription } from './index';
import type { EmojiPickerConfig, MarkdownMentionPreviewContext, ShortcutConfig } from './types';
import type {
  Severity,
  User,
  Tag as TagType,
  Column,
  MentionableTask
} from '../../views/CreateTask/create_task/types';

type Props = {
  submitTask: () => void;
  description: string;
  onDescriptionChange: (description: string) => void;
  fullHeight: boolean;
  assignees: number[];
  onAssigneesChange: (assignees: number[]) => void;
  watchers: number[];
  onWatchersChange: (watchers: number[]) => void;
  guestVisible: boolean;
  canEditAssignees: boolean;
  assignableUsers: User[];
  severity: Severity;
  onSeverityChange: (severity: Severity) => void;
  canEditSeverity: boolean;
  status?: number;
  onStatusChange: (columnId: number) => void;
  columns: Column[];
  canEditStatus: boolean;
  tags: TagType[];
  onTagsChange: (tags: TagType[]) => void;
  availableTags: TagType[];
  canEditTags: boolean;
  createTagsViaNewTask: boolean;
  setCaretPosition?: (position: number) => void;
  closePopoverWhenTypingContinues?: () => void;
  textAreaRef?: HTMLTextAreaElement | null;
  setTextAreaRef?: (ref: HTMLTextAreaElement) => void;
  mentionableTasks: MentionableTask[];
  currentUserId?: number | string | null;
  descriptionPlaceholder?: string | null;
  emojiPickerConfig?: EmojiPickerConfig | null;
  show?: boolean;
  dueAt?: string | null;
  onDueDateChange?: (dueAt: string | null) => void;
  canEditDueDate?: boolean;
};

export const CreateTaskRichTextAdapter = (props: Props) => {
  const {
    submitTask,
    description,
    onDescriptionChange,
    fullHeight,
    assignees,
    onAssigneesChange,
    watchers,
    onWatchersChange,
    guestVisible,
    canEditAssignees,
    assignableUsers,
    severity,
    onSeverityChange,
    canEditSeverity,
    status,
    onStatusChange,
    columns,
    canEditStatus,
    tags,
    onTagsChange,
    availableTags,
    canEditTags,
    createTagsViaNewTask,
    mentionableTasks,
    currentUserId,
    descriptionPlaceholder,
    emojiPickerConfig,
    show = true,
    dueAt,
    onDueDateChange,
    canEditDueDate
  } = props;

  const mentionRenderContext = useMemo(
    (): MarkdownMentionPreviewContext => ({
      descriptionMarkdown: description,
      projectMembers: assignableUsers.filter(u => u.role !== 'guest'),
      projectUsers: assignableUsers,
      allUsers: assignableUsers,
      includeGuests: true,
      mentionableTasks
    }),
    [description, assignableUsers, mentionableTasks]
  );

  const shortcutConfig: ShortcutConfig = {
    assignees,
    canEditAssignees,
    assignableUsers,
    onAssigneesChange,
    watcherIds: watchers,
    onWatchersChange,
    guestVisible,
    status,
    canEditStatus,
    onStatusChange,
    columns,
    tags,
    canEditTags,
    onTagsChange,
    availableTags,
    createTagsViaNewTask,
    severity,
    canEditSeverity,
    onSeverityChange,
    mentionableTasks,
    currentUserId,
    dueAt: dueAt ?? null,
    canEditDueDate: !!canEditDueDate,
    onDueDateChange: onDueDateChange || (() => {})
  };

  return (
    <div style={{ display: show ? undefined : 'none' }}>
      <RichTextDescription
        value={description}
        onChange={onDescriptionChange}
        placeholder={descriptionPlaceholder ?? undefined}
        emptyTabFillValue={descriptionPlaceholder ?? null}
        autoFocus={show}
        fullHeight={fullHeight}
        shortcutConfig={shortcutConfig}
        submitTask={submitTask}
        emojiPickerConfig={emojiPickerConfig ?? undefined}
        mentionRenderContext={mentionRenderContext}
        inputDataCy={show ? 'create-task-description-input' : undefined}
        getContainer={emojiPickerConfig?.getContainer}
      />
    </div>
  );
};

export default CreateTaskRichTextAdapter;
